import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import RangeTimelineSlider from '../../components/RangeTimelineSlider.js'
import { DbSelector, WelcomeOverlay } from '../TrackPointViewer/components.js'
import { deleteDB, listDBs, loadDB, saveDB, type CachedDbMeta } from '../TrackPointViewer/db-cache.js'
import {
  dedupeVisits,
  extractVisitRecords,
  formatDuration,
  initSql,
  lowerBound,
  parseCsvToTrackPoints,
  toEpoch,
  toSGDate,
  toSGTimeFull,
  upperBound,
} from '../TrackPointViewer/helpers.js'
import {
  DEFAULT_SOURCE_FILTERS,
  SOURCE_FALLBACK_LOCATION,
  SOURCE_LIVE_UPDATES,
  SOURCE_SLC,
  type SourceFilters,
  type TrackPoint,
  type VisitRecord,
} from '../TrackPointViewer/types.js'
import { readInitialDateRange, readInitialDbParam, useUrlSync } from '../TrackPointViewer/use-url-sync.js'
import { FishTankScene } from './three-scene.js'

const SG_OFFSET_MS = 8 * 3_600_000
const MINUTE_MS = 60_000
const HOUR_MS = 3_600_000
const DAY_MS = 24 * HOUR_MS
const MIN_SLIDER_VIEW_SPAN = 6 * HOUR_MS
const MAX_COMFORTABLE_SLIDER_VIEW_SPAN = 2 * DAY_MS
const MAX_SLIDER_TICKS = 240
const SLIDER_TICK_STEPS = [
  30 * MINUTE_MS,
  HOUR_MS,
  3 * HOUR_MS,
  6 * HOUR_MS,
  12 * HOUR_MS,
  DAY_MS,
  2 * DAY_MS,
  7 * DAY_MS,
  30 * DAY_MS,
] as const

/** "YYYY-MM-DDTHH:MM" (SGT local) → epoch ms. */
function sgLocalToEpoch(s: string): number {
  if (!s) return 0
  return Date.parse(s + ':00Z') - SG_OFFSET_MS
}

/** epoch ms → "YYYY-MM-DDTHH:MM" (SGT local, for datetime-local input). */
function epochToSGLocal(ms: number): string {
  return new Date(ms + SG_OFFSET_MS).toISOString().slice(0, 16)
}

/** Generate tick positions for the timeline slider, matching GPS Debug. */
function generateSliderTicks(lo: number, hi: number, densityRange = hi - lo): number[] {
  let step: number
  if (densityRange >= 3 * DAY_MS) step = DAY_MS
  else if (densityRange >= 12 * HOUR_MS) step = 3 * HOUR_MS
  else if (densityRange >= 3 * HOUR_MS) step = HOUR_MS
  else step = 30 * MINUTE_MS
  while ((hi - lo) / step > MAX_SLIDER_TICKS) {
    const idx = SLIDER_TICK_STEPS.findIndex((candidate) => candidate >= step)
    const next = SLIDER_TICK_STEPS[Math.min(idx + 1, SLIDER_TICK_STEPS.length - 1)]
    if (!next || next === step) break
    step = next
  }
  const firstK = Math.ceil((lo + SG_OFFSET_MS) / step)
  const lastK = Math.floor((hi + SG_OFFSET_MS) / step)
  const out: number[] = []
  for (let k = firstK; k <= lastK; k++) out.push(k * step - SG_OFFSET_MS)
  return out
}

/** Format a slider tick. Uses "MM-DD" for multi-day spans, else "MM-DD HH:mm". */
function formatSliderTick(ms: number, rangeMs: number): string {
  const iso = new Date(ms + SG_OFFSET_MS).toISOString()
  if (rangeMs >= 3 * DAY_MS) return iso.slice(5, 10)
  return iso.slice(5, 10) + ' ' + iso.slice(11, 16)
}

function sourceFilterPredicate(filters: SourceFilters) {
  return (p: TrackPoint) => {
    if (p.source === SOURCE_LIVE_UPDATES && !filters[SOURCE_LIVE_UPDATES]) return false
    if (p.source === SOURCE_FALLBACK_LOCATION && !filters[SOURCE_FALLBACK_LOCATION]) return false
    if (p.source === SOURCE_SLC && !filters[SOURCE_SLC]) return false
    return true
  }
}

function visitsInRange(visits: VisitRecord[], fromEpoch: number, toEpochMs: number): VisitRecord[] {
  return visits.filter((v) => {
    const arrEpoch = toEpoch(v.arrivalDate)
    if (v.departureDate) {
      const depEpoch = toEpoch(v.departureDate)
      return depEpoch >= fromEpoch && arrEpoch <= toEpochMs
    }
    return arrEpoch >= fromEpoch && arrEpoch <= toEpochMs
  })
}

function computeDataRange(points: TrackPoint[], visits: VisitRecord[]): [number, number] | null {
  let min = Infinity
  let max = -Infinity

  for (const p of points) {
    const t = Number.isFinite(p.epochMs) ? p.epochMs : toEpoch(p.timestamp)
    if (t < min) min = t
    if (t > max) max = t
  }

  for (const v of visits) {
    const arrival = toEpoch(v.arrivalDate)
    if (arrival < min) min = arrival
    if (arrival > max) max = arrival

    if (v.departureDate) {
      const departure = toEpoch(v.departureDate)
      if (departure < min) min = departure
      if (departure > max) max = departure
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) return null
  return [min, max]
}

function clampRangeToDataRange(range: [number, number], dataRange: [number, number]): [number, number] {
  let from = Math.max(dataRange[0], Math.min(dataRange[1], range[0]))
  let to = Math.max(dataRange[0], Math.min(dataRange[1], range[1]))
  if (to < from) [from, to] = [to, from]
  if (to === from) {
    to = Math.min(dataRange[1], from + 5 * MINUTE_MS)
    from = Math.max(dataRange[0], to - 5 * MINUTE_MS)
  }
  return [from, to]
}

export default function TrackPoints3D() {
  const sceneContainerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<FishTankScene | null>(null)

  const [allPoints, setAllPoints] = useState<TrackPoint[]>([])
  const [allVisits, setAllVisits] = useState<VisitRecord[]>([])
  const [filtered, setFiltered] = useState<TrackPoint[]>([])
  const [dbName, setDbName] = useState('')
  const [cachedDbs, setCachedDbs] = useState<CachedDbMeta[]>([])
  const [activeDbId, setActiveDbId] = useState<string | null>(null)
  const [showDbDropdown, setShowDbDropdown] = useState(false)

  const [dtFrom, setDtFrom] = useState('')
  const [dtTo, setDtTo] = useState('')
  const [showPointList, setShowPointList] = useState(false)
  const [filters, setFilters] = useState<SourceFilters>(DEFAULT_SOURCE_FILTERS)

  const [stats, setStats] = useState('')
  const [showMobileSidebar, setShowMobileSidebar] = useState(false)
  const [frozenSliderViewSpan, setFrozenSliderViewSpan] = useState<number | undefined | null>(null)

  const dataRange = useMemo<[number, number] | null>(() => {
    return computeDataRange(allPoints, allVisits)
  }, [allPoints, allVisits])

  const windowRange = useMemo<[number, number]>(() => {
    if (!dataRange) return [0, 0]
    const s = dtFrom ? sgLocalToEpoch(dtFrom) : dataRange[0]
    const e = dtTo ? sgLocalToEpoch(dtTo) : dataRange[1]
    return [s, e]
  }, [dtFrom, dtTo, dataRange])

  const handleWindowChange = useCallback((next: [number, number]) => {
    setDtFrom(epochToSGLocal(next[0]))
    setDtTo(epochToSGLocal(next[1]))
  }, [])

  const { setDbInUrl, clearDbFromUrl } = useUrlSync(dtFrom, dtTo)

  const computedSliderViewSpan = useMemo(() => {
    if (!dataRange) return undefined
    const dataSpan = dataRange[1] - dataRange[0]
    const selectedSpan = Math.max(MINUTE_MS, Math.min(dataSpan, Math.abs(windowRange[1] - windowRange[0]) || dataSpan))
    const comfortableSpan = Math.max(
      selectedSpan,
      Math.max(MIN_SLIDER_VIEW_SPAN, Math.min(MAX_COMFORTABLE_SLIDER_VIEW_SPAN, selectedSpan * 4)),
    )
    const next = Math.min(dataSpan, comfortableSpan)
    return next < dataSpan ? next : undefined
  }, [dataRange, windowRange])
  const sliderViewSpan = frozenSliderViewSpan === null ? computedSliderViewSpan : frozenSliderViewSpan
  const handleSliderDragStart = useCallback(() => {
    setFrozenSliderViewSpan(computedSliderViewSpan)
  }, [computedSliderViewSpan])
  const handleSliderDragEnd = useCallback(() => {
    setFrozenSliderViewSpan(null)
  }, [])

  const sliderTicks = useMemo(() => {
    if (!dataRange) return undefined
    return generateSliderTicks(dataRange[0], dataRange[1], sliderViewSpan ?? dataRange[1] - dataRange[0])
  }, [dataRange, sliderViewSpan])

  const sliderTickFormatter = useCallback(
    (ms: number) => {
      const range = sliderViewSpan ?? (dataRange ? dataRange[1] - dataRange[0] : 0)
      return formatSliderTick(ms, range)
    },
    [dataRange, sliderViewSpan],
  )

  useEffect(() => {
    if (!sceneContainerRef.current) return
    if (sceneRef.current) return
    const apiKey =
      (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_GOOGLE_MAPS_API_KEY || ''
    sceneRef.current = new FishTankScene({
      container: sceneContainerRef.current,
      apiKey,
    })
    return () => {
      sceneRef.current?.dispose()
      sceneRef.current = null
    }
  }, [])

  const loadFile = useCallback(
    async (buffer: ArrayBuffer, fileName: string, cacheId?: string) => {
      let points: TrackPoint[]
      let visits: VisitRecord[] = []

      if (fileName.endsWith('.csv')) {
        const text = new TextDecoder().decode(buffer)
        points = parseCsvToTrackPoints(text)
        if (points.length === 0) {
          window.alert('No track point data found in CSV file.')
          return
        }
      } else {
        const SQL = await initSql()
        const db = new SQL.Database(new Uint8Array(buffer))
        const results = db.exec(`
        SELECT id, latitude, longitude, altitude, horizontalAccuracy, speed, course,
               timestamp, source, segmentIndex
        FROM TrackPoint ORDER BY timestamp ASC
      `)

        if (results.length === 0) {
          db.close()
          window.alert('No TrackPoint data found in this database.')
          return
        }

        const cols: string[] = results[0]?.columns ?? []
        const rows: unknown[][] = results[0]?.values ?? []
        points = rows.map((row: unknown[]) => {
          const obj: Record<string, unknown> = {}
          cols.forEach((c: string, idx: number) => (obj[c] = row[idx]))
          return obj as unknown as TrackPoint
        })

        visits = dedupeVisits(extractVisitRecords(db))
        db.close()
      }

      for (const p of points) {
        p.epochMs = toEpoch(p.timestamp)
      }
      points.sort((a, b) => a.epochMs - b.epochMs)
      const loadedDataRange = computeDataRange(points, visits)

      let dbId = cacheId
      if (!dbId) {
        dbId = await saveDB(fileName, buffer)
        setCachedDbs(await listDBs())
      }
      setActiveDbId(dbId)
      setDbInUrl(dbId, fileName)

      setAllPoints(points)
      setAllVisits(visits)
      setDbName(fileName)

      const saved = readInitialDateRange()
      if (saved && loadedDataRange) {
        const clamped = clampRangeToDataRange([sgLocalToEpoch(saved.from), sgLocalToEpoch(saved.to)], loadedDataRange)
        setDtFrom(epochToSGLocal(clamped[0]))
        setDtTo(epochToSGLocal(clamped[1]))
      } else if (loadedDataRange) {
        setDtFrom(epochToSGLocal(loadedDataRange[0]))
        setDtTo(epochToSGLocal(loadedDataRange[1]))
      } else {
        const firstPoint = points[0]
        const lastPoint = points[points.length - 1]
        if (firstPoint && lastPoint) {
          const lastDate = toSGDate(lastPoint.timestamp)
          setDtFrom(lastDate + 'T00:00')
          setDtTo(lastDate + 'T23:59')
        }
      }
    },
    [setDbInUrl],
  )

  useEffect(() => {
    listDBs()
      .then(setCachedDbs)
      .catch(() => {})

    const parsed = readInitialDbParam()
    if (!parsed) return

    loadDB(parsed.id).then((entry) => {
      if (entry) {
        loadFile(entry.buffer, entry.fileName, entry.id)
      } else {
        clearDbFromUrl()
      }
    })
  }, [loadFile, clearDbFromUrl])

  const visibleVisits = useMemo(() => {
    if (allVisits.length === 0 || !dataRange) return []
    return visitsInRange(allVisits, windowRange[0], windowRange[1])
  }, [allVisits, dataRange, windowRange])

  useEffect(() => {
    if (!allPoints.length || !dataRange) {
      setFiltered([])
      setStats('')
      sceneRef.current?.setData({
        trackPoints: [],
        visits: [],
        filters,
        windowStart: 0,
        windowEnd: 1,
        mode: '3d',
      })
      return
    }

    const startIdx = lowerBound(allPoints, windowRange[0])
    const endIdx = upperBound(allPoints, windowRange[1])
    const slice = allPoints.slice(startIdx, endIdx)
    const needSourceFilter = !filters[SOURCE_LIVE_UPDATES] || !filters[SOURCE_FALLBACK_LOCATION] || !filters[SOURCE_SLC]
    const nextFiltered = needSourceFilter ? slice.filter(sourceFilterPredicate(filters)) : slice
    setFiltered(nextFiltered)

    sceneRef.current?.setData({
      trackPoints: nextFiltered,
      visits: visibleVisits,
      filters,
      windowStart: windowRange[0],
      windowEnd: windowRange[1],
      mode: '3d',
    })

    const visitCount = filters.visits ? visibleVisits.length : 0
    setStats(`${nextFiltered.length.toLocaleString()} points · ${visitCount.toLocaleString()} CLVisits · 3D`)
  }, [allPoints, dataRange, filters, visibleVisits, windowRange])

  const focusPoint = useCallback((_idx: number) => {
    sceneRef.current?.setView('default')
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => e.preventDefault(), [])
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer.files[0]
      if (!file) return
      const reader = new window.FileReader()
      reader.onload = () => loadFile(reader.result as ArrayBuffer, file.name)
      reader.readAsArrayBuffer(file)
    },
    [loadFile],
  )
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new window.FileReader()
      reader.onload = () => loadFile(reader.result as ArrayBuffer, file.name)
      reader.readAsArrayBuffer(file)
    },
    [loadFile],
  )

  const switchToDb = useCallback(
    async (meta: CachedDbMeta) => {
      if (meta.id === activeDbId) {
        setShowDbDropdown(false)
        return
      }
      const entry = await loadDB(meta.id)
      if (entry) {
        loadFile(entry.buffer, entry.fileName, entry.id)
      } else {
        setCachedDbs(await listDBs())
      }
      setShowDbDropdown(false)
    },
    [activeDbId, loadFile],
  )

  const handleDeleteDb = useCallback(
    async (e: React.MouseEvent, id: string) => {
      e.stopPropagation()
      await deleteDB(id)
      const updated = await listDBs()
      setCachedDbs(updated)
      if (id === activeDbId) {
        setAllPoints([])
        setAllVisits([])
        setDbName('')
        setActiveDbId(null)
        clearDbFromUrl()
      }
    },
    [activeDbId, clearDbFromUrl],
  )

  const hasData = allPoints.length > 0

  return (
    <div
      className="relative h-[calc(100vh-120px)] overflow-hidden bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100 sm:flex"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {hasData && (
        <div
          className={`absolute inset-x-0 bottom-0 z-20 flex max-h-[70vh] flex-col overflow-y-auto bg-white text-xs text-slate-900 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] transition-transform duration-200 dark:bg-slate-900 dark:text-slate-100 dark:shadow-[0_-2px_16px_rgba(0,0,0,0.45)] sm:relative sm:inset-auto sm:z-auto sm:max-h-none sm:w-80 sm:flex-shrink-0 sm:translate-y-0 sm:border-r sm:border-gray-200 sm:shadow-none sm:dark:border-slate-700 ${showMobileSidebar ? 'translate-y-0' : 'translate-y-full sm:translate-y-0'}`}
        >
          <div className="flex justify-center py-1.5 sm:hidden" onClick={() => setShowMobileSidebar(false)}>
            <div className="h-1 w-8 rounded-full bg-gray-300 dark:bg-slate-600" />
          </div>

          <DbSelector
            dbName={dbName}
            activeDbId={activeDbId}
            showDropdown={showDbDropdown}
            setShowDropdown={setShowDbDropdown}
            cachedDbs={cachedDbs}
            onSwitch={switchToDb}
            onDelete={handleDeleteDb}
            onFileChange={handleFileChange}
          />

          <div className="flex flex-col gap-1 border-b border-gray-100 px-3 py-2 dark:border-slate-700">
            <input
              type="datetime-local"
              value={dtFrom}
              onChange={(e) => setDtFrom(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
            />
            <input
              type="datetime-local"
              value={dtTo}
              onChange={(e) => setDtTo(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[color-scheme:dark]"
            />
          </div>

          {dataRange && (
            <div className="border-b border-gray-100 px-3 py-2 dark:border-slate-700">
              <RangeTimelineSlider
                min={dataRange[0]}
                max={dataRange[1]}
                step={10 * MINUTE_MS}
                value={windowRange}
                minSpan={5 * MINUTE_MS}
                maxSpan={dataRange[1] - dataRange[0]}
                onChange={handleWindowChange}
                onDragStart={handleSliderDragStart}
                onDragEnd={handleSliderDragEnd}
                formatTick={sliderTickFormatter}
                ticks={sliderTicks}
                viewSpan={sliderViewSpan}
              />
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-3 py-2 text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_LIVE_UPDATES]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_LIVE_UPDATES]: e.target.checked }))}
              />
              <span className="text-[#34C759]">●</span>liveUpdates
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_FALLBACK_LOCATION]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_FALLBACK_LOCATION]: e.target.checked }))}
              />
              <span className="text-[#FF9500]">●</span>startUpdating
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters[SOURCE_SLC]}
                onChange={(e) => setFilters((f) => ({ ...f, [SOURCE_SLC]: e.target.checked }))}
              />
              <span className="text-[#AF52DE]">●</span>slc
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={filters.visits}
                onChange={(e) => setFilters((f) => ({ ...f, visits: e.target.checked }))}
              />
              <span className="text-[#007AFF]">●</span>CLVisit
            </label>
            <button
              onClick={() => setShowPointList((v) => !v)}
              className="rounded bg-gray-200 px-2 py-0.5 text-gray-700 hover:bg-gray-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
            >
              {showPointList ? 'Hide List' : 'List'}
            </button>
          </div>

          {stats && (
            <div className="border-b border-gray-100 px-3 py-2 text-gray-600 dark:border-slate-700 dark:text-slate-300">
              {stats}
            </div>
          )}

          {showPointList && (
            <div className="max-h-60 overflow-y-auto border-b border-gray-100 dark:border-slate-700">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="sticky top-0 bg-gray-100 text-left dark:bg-slate-800 dark:text-slate-200">
                    <th className="px-2 py-1">#</th>
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1">Acc</th>
                    <th className="px-2 py-1">Spd</th>
                    <th className="px-2 py-1">Gap</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, i) => {
                    const gapSec = i > 0 ? (toEpoch(p.timestamp) - toEpoch(filtered[i - 1]!.timestamp)) / 1000 : 0
                    const isGap = gapSec >= 120
                    const isLowAcc = p.horizontalAccuracy != null && p.horizontalAccuracy >= 65
                    return (
                      <tr
                        key={p.id}
                        className={`cursor-pointer border-t border-gray-100 hover:bg-blue-50 dark:border-slate-700 dark:hover:bg-blue-500/10 ${
                          isGap
                            ? 'bg-yellow-50 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200'
                            : isLowAcc
                              ? 'text-red-600 dark:text-red-300'
                              : 'dark:text-slate-200'
                        }`}
                        onClick={() => focusPoint(i)}
                      >
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">{toSGTimeFull(p.timestamp)}</td>
                        <td className="px-2 py-1">
                          {p.horizontalAccuracy != null ? p.horizontalAccuracy.toFixed(0) : '—'}
                        </td>
                        <td className="px-2 py-1">{p.speed != null ? (p.speed * 3.6).toFixed(1) : '—'}</td>
                        <td className="px-2 py-1">{i > 0 ? formatDuration(gapSec) : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="absolute inset-0 bg-slate-200 dark:bg-slate-950 sm:relative sm:flex-1">
        <div ref={sceneContainerRef} className="h-full w-full" />

        {!hasData && (
          <WelcomeOverlay
            cachedDbs={cachedDbs}
            onSwitch={switchToDb}
            onDelete={handleDeleteDb}
            onFileChange={handleFileChange}
          />
        )}

        {hasData && (
          <div className="absolute right-3 top-3 z-10 flex flex-col gap-1 rounded-xl bg-white/90 p-1.5 text-xs shadow-lg backdrop-blur-sm dark:bg-slate-900/90 dark:shadow-black/40">
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.setView('default')}
            >
              斜视
            </button>
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.setView('top')}
            >
              俯视
            </button>
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.setView('front')}
            >
              正视
            </button>
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.setView('side')}
            >
              侧视
            </button>
            <div className="my-1 h-px bg-gray-200 dark:bg-slate-600" />
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.rotateAzimuth(-Math.PI / 2)}
            >
              ↺ 90°
            </button>
            <button
              className="rounded bg-gray-100 px-2 py-1 text-slate-700 hover:bg-gray-200 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              onClick={() => sceneRef.current?.rotateAzimuth(Math.PI / 2)}
            >
              ↻ 90°
            </button>
          </div>
        )}

        {hasData && !showMobileSidebar && (
          <button
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white px-4 py-2 text-xs font-medium text-gray-700 shadow-lg active:bg-gray-100 dark:bg-slate-800 dark:text-slate-100 dark:shadow-black/40 dark:active:bg-slate-700 sm:hidden"
            onClick={() => setShowMobileSidebar(true)}
          >
            Controls ▲
          </button>
        )}
      </div>

      {hasData && showMobileSidebar && (
        <div
          className="absolute inset-0 z-10 bg-black/20 dark:bg-black/40 sm:hidden"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}
    </div>
  )
}
